import { batch, createEffect, createMemo } from "solid-js"
import { createStore } from "solid-js/store"
import { DateTime } from "luxon"
import { filter, firstBy, flat, groupBy, mapValues, pipe, uniqueBy, values } from "remeda"
import { createSimpleContext } from "@openanalyst-ai/ui/context"
import { useProviders } from "@/hooks/use-providers"
import { Persist, persisted } from "@/utils/persist"
import { isModelVisible, resolveProviderSelection, type ModelKey } from "./models-visibility"

export type { ModelKey } from "./models-visibility"
export { isModelVisible } from "./models-visibility"

type Visibility = "show" | "hide"
type User = ModelKey & { visibility: Visibility; favorite?: boolean }
type ProviderSelection = { enabled: boolean; selected: string[] }
type Store = {
  user: User[]
  recent: ModelKey[]
  variant?: Record<string, string | undefined>
  providers: Record<string, ProviderSelection | undefined>
  providerSelectionMigrated: boolean
  providerSelectionDefaultOffMigrated: boolean
}

const RECENT_LIMIT = 5

function modelKey(model: ModelKey) {
  return `${model.providerID}:${model.modelID}`
}

export const { use: useModels, provider: ModelsProvider } = createSimpleContext({
  name: "Models",
  gate: false,
  init: () => {
    const providers = useProviders()

    const [store, setStore, _, ready] = persisted(
      Persist.global("model", ["model.v1"]),
      createStore<Store>({
        user: [],
        recent: [],
        variant: {},
        providers: {},
        providerSelectionMigrated: false,
        providerSelectionDefaultOffMigrated: false,
      }),
    )

    const available = createMemo(() =>
      providers.connected().flatMap((p) =>
        Object.values(p.models).map((m) => ({
          ...m,
          provider: p,
        })),
      ),
    )

    const release = createMemo(
      () =>
        new Map(
          available().map((model) => {
            const parsed = DateTime.fromISO(model.release_date)
            return [modelKey({ providerID: model.provider.id, modelID: model.id }), parsed] as const
          }),
        ),
    )

    const latest = createMemo(() =>
      pipe(
        available(),
        filter(
          (x) =>
            Math.abs(
              (release().get(modelKey({ providerID: x.provider.id, modelID: x.id })) ?? DateTime.invalid("invalid"))
                .diffNow()
                .as("months"),
            ) < 6,
        ),
        groupBy((x) => x.provider.id),
        mapValues((models) =>
          pipe(
            models,
            groupBy((x) => x.family),
            values(),
            (groups) =>
              groups.flatMap((g) => {
                const first = firstBy(g, [(x) => x.release_date, "desc"])
                return first ? [{ modelID: first.id, providerID: first.provider.id }] : []
              }),
          ),
        ),
        values(),
        flat(),
      ),
    )

    const latestSet = createMemo(() => new Set(latest().map((x) => modelKey(x))))

    const visibility = createMemo(() => {
      const map = new Map<string, Visibility>()
      for (const item of store.user) map.set(`${item.providerID}:${item.modelID}`, item.visibility)
      return map
    })

    const list = createMemo(() =>
      available().map((m) => ({
        ...m,
        name: m.name.replace("(latest)", "").trim(),
        latest: m.name.includes("(latest)"),
      })),
    )

    const find = (key: ModelKey) => list().find((m) => m.id === key.modelID && m.provider.id === key.providerID)

    const legacyVisible = (model: ModelKey) =>
      isModelVisible(model, {
        visibility: visibility(),
        latest: latestSet(),
        release: release(),
      })

    const providerDefaultSelection = (providerID: string) => {
      const provider = providers.connected().find((item) => item.id === providerID)
      if (!provider) return []
      const configured = providers.default()[providerID]
      const fallback = configured && provider.models[configured] ? configured : Object.values(provider.models)[0]?.id
      return fallback ? [fallback] : []
    }

    const providerSelections = createMemo(
      () =>
        new Map(
          providers
            .connected()
            .filter((provider) => provider.id !== "openanalyst")
            .map((provider) => {
              const current = store.providers[provider.id]
              if (current) {
                return [
                  provider.id,
                  { enabled: current.enabled, selected: new Set(current.selected) },
                ] as const
              }
              return [
                provider.id,
                { enabled: false, selected: new Set(providerDefaultSelection(provider.id)) },
              ] as const
            }),
        ),
    )

    function update(model: ModelKey, state: Visibility) {
      const index = store.user.findIndex((x) => x.modelID === model.modelID && x.providerID === model.providerID)
      if (index >= 0) {
        setStore("user", index, (current) => ({ ...current, visibility: state }))
        return
      }
      setStore("user", store.user.length, { ...model, visibility: state })
    }

    createEffect(() => {
      if (!ready() || (store.providerSelectionMigrated && store.providerSelectionDefaultOffMigrated)) return
      const connected = providers.connected()
      if (connected.length === 0) return

      batch(() => {
        connected
          .filter((provider) => provider.id !== "openanalyst")
          .forEach((provider) => {
            const current = store.providers[provider.id]
            setStore(
              "providers",
              provider.id,
              resolveProviderSelection({
                enabled: false,
                selected:
                  current?.selected ??
                  Object.values(provider.models)
                    .filter((model) => legacyVisible({ providerID: provider.id, modelID: model.id }))
                    .map((model) => model.id),
                fallback: providerDefaultSelection(provider.id),
              }),
            )
          })
        setStore("providerSelectionMigrated", true)
        setStore("providerSelectionDefaultOffMigrated", true)
      })
    })

    const visible = (model: ModelKey) => {
      return isModelVisible(model, {
        visibility: visibility(),
        latest: latestSet(),
        release: release(),
        provider:
          store.providerSelectionMigrated && store.providerSelectionDefaultOffMigrated
            ? providerSelections().get(model.providerID)
            : undefined,
      })
    }

    const setVisibility = (model: ModelKey, state: boolean) => {
      if (model.providerID === "openanalyst") return
      const current = providerSelections().get(model.providerID) ?? { enabled: false, selected: new Set<string>() }
      const selected = state
        ? [...new Set([...current.selected, model.modelID])]
        : [...current.selected].filter((modelID) => modelID !== model.modelID)
      setStore("providers", model.providerID, {
        enabled: state ? true : selected.length > 0 && current.enabled,
        selected,
      })
      setStore("providerSelectionMigrated", true)
      setStore("providerSelectionDefaultOffMigrated", true)
      update(model, state ? "show" : "hide")
    }

    const providerEnabled = (providerID: string) => {
      if (providerID === "openanalyst") return true
      const current = providerSelections().get(providerID)
      return !!current?.enabled && current.selected.size > 0
    }

    const providerSelected = (providerID: string) => {
      const available = new Set(
        list()
          .filter((model) => model.provider.id === providerID)
          .map((model) => model.id),
      )
      if (providerID === "openanalyst") return [...available]
      return [...(providerSelections().get(providerID)?.selected ?? [])].filter((modelID) => available.has(modelID))
    }

    const setProviderEnabled = (providerID: string, state: boolean) => {
      if (providerID === "openanalyst") return
      const current = providerSelections().get(providerID) ?? { enabled: false, selected: new Set<string>() }
      setStore(
        "providers",
        providerID,
        resolveProviderSelection({
          enabled: state,
          selected: [...current.selected],
          fallback: providerDefaultSelection(providerID),
        }),
      )
      setStore("providerSelectionMigrated", true)
      setStore("providerSelectionDefaultOffMigrated", true)
    }

    const setProviderModels = (providerID: string, selected: string[]) => {
      if (providerID === "openanalyst") return
      const available = new Set(
        list()
          .filter((model) => model.provider.id === providerID)
          .map((model) => model.id),
      )
      const next = [...new Set(selected)].filter((modelID) => available.has(modelID))
      setStore("providers", providerID, resolveProviderSelection({ enabled: true, selected: next }))
      setStore("providerSelectionMigrated", true)
      setStore("providerSelectionDefaultOffMigrated", true)
    }

    const push = (model: ModelKey) => {
      const uniq = uniqueBy([model, ...store.recent], (x) => `${x.providerID}:${x.modelID}`)
      if (uniq.length > RECENT_LIMIT) uniq.pop()
      setStore("recent", uniq)
    }

    const variantKey = (model: ModelKey) => `${model.providerID}/${model.modelID}`
    const getVariant = (model: ModelKey) => store.variant?.[variantKey(model)]

    const setVariant = (model: ModelKey, value: string | undefined) => {
      const key = variantKey(model)
      if (!store.variant) {
        setStore("variant", { [key]: value })
        return
      }
      setStore("variant", key, value)
    }

    return {
      ready,
      list,
      find,
      visible,
      setVisibility,
      provider: {
        enabled: providerEnabled,
        selected: providerSelected,
        setEnabled: setProviderEnabled,
        setModels: setProviderModels,
      },
      recent: {
        list: createMemo(() => store.recent),
        push,
      },
      variant: {
        get: getVariant,
        set: setVariant,
      },
    }
  },
})
